/**
 * @file formatDate.ts
 * @description
 * -----------------------------------------------------
 * Utilidades para formatear la fecha
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-03-11
 * @modified 2025-06-25
 * @author DLTCode
 * @author Daniel Sentamans
 * @author Nacho Moya
 * @copyright © DLTCode
 * @license Private
 * */

"use client";

import { c as _c } from "react/compiler-runtime";
import { DateTimeFormatOptions, useFormatter } from "next-intl";
import { useCallback, useEffect, useState } from "react";
const dateFormatOptions: DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric"
};
const timeFormatOptions: DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
  timeZoneName: "short"
};
export function useFormatDateTime(isoDate) {
  const $ = _c(4);
  const format = useFormatter();
  const [formattedDate, setFormattedDate] = useState("");
  let t0;
  let t1;
  if ($[0] !== format || $[1] !== isoDate) {
    t0 = () => {
      const dateTime = new Date(isoDate);
      if (isNaN(dateTime.getTime())) {
        setFormattedDate(typeof isoDate === "string" ? isoDate : "");
        return;
      }
      const formattedDate_0 = format.dateTime(dateTime, dateFormatOptions);
      const formattedTime = format.dateTime(dateTime, timeFormatOptions);
      setFormattedDate(`${formattedDate_0} ${formattedTime}`);
    };
    t1 = [format, isoDate];
    $[0] = format;
    $[1] = isoDate;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  return formattedDate;
}
export function useFormatDateTimeCallback() {
  const $ = _c(2);
  const format = useFormatter();
  let t0;
  if ($[0] !== format) {
    t0 = (isoDate, options) => {
      const dateTime = new Date(isoDate);
      if (isNaN(dateTime.getTime())) {
        return typeof isoDate === "string" ? isoDate : "";
      }
      const formattedDate = format.dateTime(dateTime, options?.date || dateFormatOptions);
      const formattedTime = format.dateTime(dateTime, options?.time || timeFormatOptions);
      return `${formattedDate} ${formattedTime}`;
    };
    $[0] = format;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export function useFormatDateCallback() {
  const $ = _c(2);
  const format = useFormatter();
  let t0;
  if ($[0] !== format) {
    t0 = isoDate => {
      const dateTime = new Date(isoDate);
      if (isNaN(dateTime.getTime())) {
        return isoDate;
      }
      return format.dateTime(dateTime, dateFormatOptions);
    };
    $[0] = format;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export function useFormatTimeCallback() {
  const $ = _c(2);
  const format = useFormatter();
  let t0;
  if ($[0] !== format) {
    t0 = (isoDate, options) => {
      const dateTime = new Date(isoDate);
      if (isNaN(dateTime.getTime())) {
        return isoDate;
      }
      return format.dateTime(dateTime, options || timeFormatOptions);
    };
    $[0] = format;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export function useFormatRelativeTimeCallback() {
  const $ = _c(2);
  const format = useFormatter();
  let t0;
  if ($[0] !== format) {
    t0 = isoDate => {
      const dateTime = new Date(isoDate);
      if (isNaN(dateTime.getTime())) {
        return typeof isoDate === "string" ? isoDate : "";
      }
      return format.relativeTime(dateTime, {});
    };
    $[0] = format;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export function useFormatDurationCallback() {
  const $ = _c(2);
  const format = useFormatter();
  let t0;
  if ($[0] !== format) {
    t0 = durationInMilliseconds => {
      if (durationInMilliseconds === 0) {
        return format.number(0, {
          style: "unit",
          unit: "second"
        });
      }
      const years = Math.floor(durationInMilliseconds / 31536000000);
      const remainingAfterYears = durationInMilliseconds % 31536000000;
      const months = Math.floor(remainingAfterYears / 2592000000);
      const remainingAfterMonths = remainingAfterYears % 2592000000;
      const weeks = Math.floor(remainingAfterMonths / 604800000);
      const remainingAfterWeeks = remainingAfterMonths % 604800000;
      const days = Math.floor(remainingAfterWeeks / 86400000);
      const remainingAfterDays = remainingAfterWeeks % 86400000;
      const hours = Math.floor(remainingAfterDays / 3600000);
      const remainingAfterHours = remainingAfterDays % 3600000;
      const minutes = Math.floor(remainingAfterHours / 60000);
      const remainingAfterMinutes = remainingAfterHours % 60000;
      const secondsWithMs = remainingAfterMinutes / 1000;
      const parts = [];
      if (years > 0) {
        parts.push(format.number(years, {
          style: "unit",
          unit: "year"
        }));
      }
      if (months > 0) {
        parts.push(format.number(months, {
          style: "unit",
          unit: "month"
        }));
      }
      if (weeks > 0) {
        parts.push(format.number(weeks, {
          style: "unit",
          unit: "week"
        }));
      }
      if (days > 0) {
        parts.push(format.number(days, {
          style: "unit",
          unit: "day"
        }));
      }
      if (hours > 0) {
        parts.push(format.number(hours, {
          style: "unit",
          unit: "hour"
        }));
      }
      if (minutes > 0) {
        parts.push(format.number(minutes, {
          style: "unit",
          unit: "minute"
        }));
      }
      if (secondsWithMs > 0 || parts.length === 0) {
        const hasDecimalPart = secondsWithMs % 1 !== 0;
        const showDecimals = parts.length === 0 && hasDecimalPart;
        if (showDecimals) {
          parts.push(`${secondsWithMs.toFixed(3)} s`);
        } else {
          if (secondsWithMs > 0) {
            parts.push(format.number(Math.floor(secondsWithMs), {
              style: "unit",
              unit: "second"
            }));
          } else {
            if (parts.length === 0) {
              parts.push(format.number(0, {
                style: "unit",
                unit: "second"
              }));
            }
          }
        }
      }
      return parts.join(" ");
    };
    $[0] = format;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}